/**
 * User Preferences Component
 * 
 * User preferences management component with:
 * - Display preferences (density, language, date/time format)
 * - Notification preferences
 * - Dashboard preferences (pagination, default sort)
 * - Automatic saving to localStorage
 * - Modern interface with tabs
 * 
 * @component
 * @param {Object} props - Component props
 * @param {boolean} props.open - Whether dialog is open
 * @param {Function} props.onClose - Callback when dialog is closed
 * @returns {JSX.Element} The user preferences component
 */
import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Tabs,
  Tab,
  Box,
  FormControl,
  FormLabel,
  FormGroup,
  FormControlLabel,
  Switch,
  Select,
  MenuItem,
  Slider,
  Typography,
  Divider,
  Alert,
  Chip,
  Grid,
  Card,
  CardContent,
  IconButton
} from '@mui/material';
import {
  Settings as SettingsIcon,
  SmartDisplay as DisplayIcon,
  Notifications as NotificationsIcon,
  TableChart as TableIcon,
  Language as LanguageIcon,
  Palette as PaletteIcon,
  Close as CloseIcon,
  RestartAlt as ResetIcon
} from '@mui/icons-material';
import LanguageSelector from '../i18n/LanguageSelector';
import { useThemeMode } from '../../contexts/ThemeContext.helpers.js';
import { useTranslation } from 'react-i18next';
import i18n from '../../i18n/react-i18n';

// Preferences by default
const defaultPreferences = {
  display: {
    density: 'standard', // compact, standard, comfortable
    language: (typeof localStorage !== 'undefined' && (localStorage.getItem('userLocale') || localStorage.getItem('i18nextLng'))) || 'en-US',
    dateFormat: 'DD/MM/YYYY',
    timeFormat: '24h',
    showAvatars: true,
    showTooltips: true,
    animationsEnabled: true
  },
  notifications: {
    emailNotifications: true,
    pushNotifications: true,
    soundEnabled: true,
    orderUpdates: true,
    stockAlerts: true,
    systemMaintenance: true,
    marketingEmails: false
  },
  tables: {
    defaultPageSize: 25,
    defaultSortOrder: 'desc',
    showRowNumbers: true,
    stickyHeaders: true,
    autoRefresh: true,
    refreshInterval: 30 // secondes
  },
  dashboard: {
    autoRefreshDashboard: true,
    dashboardRefreshInterval: 60, // secondes
    showWelcomeMessage: true,
    compactWidgets: false,
    favoritePages: []
  }
};

const UserPreferences = ({ open, onClose }) => {
  const { t } = useTranslation(['common', 'labels']);

  const [activeTab, setActiveTab] = useState(0);
  const [preferences, setPreferences] = useState(defaultPreferences);
  const [hasChanges, setHasChanges] = useState(false);
  const { darkMode: _darkMode } = useThemeMode();

  // Loadsr les preferences au montage
  useEffect(() => {
    const savedPreferences = localStorage.getItem('wms-user-preferences');
    if (savedPreferences) {
      try {
        const parsed = JSON.parse(savedPreferences);
        setPreferences({ ...defaultPreferences, ...parsed });
      } catch (error) {
        void 0;
      }
    }
  }, []);

  // Save preferences and apply language change
  const savePreferences = () => {
    localStorage.setItem('wms-user-preferences', JSON.stringify(preferences));
    
    // Apply language change to i18n
    if (preferences.display.language) {
      i18n.changeLanguage(preferences.display.language);
      localStorage.setItem('wms-user-locale', preferences.display.language);
      localStorage.setItem('i18nextLng', preferences.display.language);
    }
    
    setHasChanges(false);
  };

  // Resetsr les preferences
  const resetPreferences = () => {
    setPreferences(defaultPreferences);
    setHasChanges(true);
  };

  // Update une preference
  const updatePreference = (category, key, value) => {
    setPreferences(prev => ({
      ...prev,
      [category]: {
        ...prev[category],
        [key]: value
      }
    }));
    setHasChanges(true);
  };

  // Display Tab
  const renderDisplayTab = () => (
    <Box sx={{ p: 2 }}>
      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                <DisplayIcon sx={{ mr: 1, verticalAlign: 'middle' }} />{t('common:preferences.display.title')}</Typography>
              
              <FormControl fullWidth margin="normal">
                <FormLabel>{t('common:preferences.display.density')}</FormLabel>
                <Select
                  value={preferences.display.density}
                  onChange={(e) => updatePreference('display', 'density', e.target.value)}
                >
                  <MenuItem value="compact">{t('common:preferences.display.compact')}</MenuItem>
                  <MenuItem value="standard">{t('common:preferences.display.standard')}</MenuItem>
                  <MenuItem value="comfortable">{t('common:preferences.display.comfortable')}</MenuItem>
                </Select>
              </FormControl>

              <Box sx={{ mt: 2 }}>
                <FormLabel>{t('common:preferences.display.language')}</FormLabel>
                <Box sx={{ mt: 1 }}>
                  <LanguageSelector
                    size="small"
                    value={preferences.display.language}
                    onChange={(val) => updatePreference('display', 'language', val)}
                  />
                </Box>
              </Box>

              <FormGroup sx={{ mt: 2 }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={preferences.display.showAvatars}
                      onChange={(e) => updatePreference('display', 'showAvatars', e.target.checked)}
                    />
                  }
                  label={t('common:preferences.display.showAvatars')}
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={preferences.display.showTooltips}
                      onChange={(e) => updatePreference('display', 'showTooltips', e.target.checked)}
                    />
                  }
                  label={t('common:preferences.display.showTooltips')}
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={preferences.display.animationsEnabled}
                      onChange={(e) => updatePreference('display', 'animationsEnabled', e.target.checked)}
                    />
                  }
                  label={t('common:preferences.display.enableAnimations')}
                />
              </FormGroup>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                <LanguageIcon sx={{ mr: 1, verticalAlign: 'middle' }} />{t('common:preferences.format.title')}</Typography>
              
              <FormControl fullWidth margin="normal">
                <FormLabel>{t('common:preferences.format.dateFormat')}</FormLabel>
                <Select
                  value={preferences.display.dateFormat}
                  onChange={(e) => updatePreference('display', 'dateFormat', e.target.value)}
                >
                  <MenuItem value="DD/MM/YYYY">DD/MM/YYYY</MenuItem>
                  <MenuItem value="MM/DD/YYYY">MM/DD/YYYY</MenuItem>
                  <MenuItem value="YYYY-MM-DD">YYYY-MM-DD</MenuItem>
                </Select>
              </FormControl>

              <FormControl fullWidth margin="normal">
                <FormLabel>{t('common:preferences.format.timeFormat')}</FormLabel>
                <Select
                  value={preferences.display.timeFormat}
                  onChange={(e) => updatePreference('display', 'timeFormat', e.target.value)}
                >
                  <MenuItem value="24h">{t('common:preferences.format.24hours')}</MenuItem>
                  <MenuItem value="12h">{t('common:preferences.format.12hours')}</MenuItem>
                </Select>
              </FormControl>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );

  // Notifications Tab
  const renderNotificationsTab = () => (
    <Box sx={{ p: 2 }}>
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            <NotificationsIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
            {t('common:preferences.notifications.title')}
          </Typography>
          
          <FormGroup>
            <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>
              {t('common:preferences.notifications.channels')}
            </Typography>
            <FormControlLabel
              control={
                <Switch
                  checked={preferences.notifications.emailNotifications}
                  onChange={(e) => updatePreference('notifications', 'emailNotifications', e.target.checked)}
                />
              }
              label={t('common:preferences.notifications.emailNotifications')}
            />
            <FormControlLabel
              control={
                <Switch
                  checked={preferences.notifications.pushNotifications}
                  onChange={(e) => updatePreference('notifications', 'pushNotifications', e.target.checked)}
                />
              }
              label={t('common:preferences.notifications.pushNotifications')}
            />
            <FormControlLabel
              control={
                <Switch
                  checked={preferences.notifications.soundEnabled}
                  onChange={(e) => updatePreference('notifications', 'soundEnabled', e.target.checked)}
                />
              }
              label={t('common:preferences.notifications.soundEnabled')}
            />

            <Divider sx={{ my: 2 }} />

            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              {t('common:preferences.notifications.types')}
            </Typography>
            <FormControlLabel
              control={
                <Switch
                  checked={preferences.notifications.orderUpdates}
                  onChange={(e) => updatePreference('notifications', 'orderUpdates', e.target.checked)}
                />
              }
              label={t('common:preferences.notifications.orderUpdates')}
            />
            <FormControlLabel
              control={
                <Switch
                  checked={preferences.notifications.stockAlerts}
                  onChange={(e) => updatePreference('notifications', 'stockAlerts', e.target.checked)}
                />
              }
              label={t('common:preferences.notifications.stockAlerts')}
            />
            <FormControlLabel
              control={
                <Switch
                  checked={preferences.notifications.systemMaintenance}
                  onChange={(e) => updatePreference('notifications', 'systemMaintenance', e.target.checked)}
                />
              }
              label={t('common:preferences.notifications.systemMaintenance')}
            />
            <FormControlLabel
              control={
                <Switch
                  checked={preferences.notifications.marketingEmails}
                  onChange={(e) => updatePreference('notifications', 'marketingEmails', e.target.checked)}
                />
              }
              label={t('common:preferences.notifications.marketingEmails')}
            />
          </FormGroup>
        </CardContent>
      </Card>
    </Box>
  );

  // Tables Tab
  const renderTablesTab = () => (
    <Box sx={{ p: 2 }}>
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            <TableIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
            {t('common:preferences.tables.title')}
          </Typography>
          
          <FormControl fullWidth margin="normal">
            <FormLabel>{t('common:preferences.tables.defaultPageSize')}</FormLabel>
            <Select
              value={preferences.tables.defaultPageSize}
              onChange={(e) => updatePreference('tables', 'defaultPageSize', e.target.value)}
            >
              <MenuItem value={10}>10</MenuItem>
              <MenuItem value={25}>25</MenuItem>
              <MenuItem value={50}>50</MenuItem>
              <MenuItem value={100}>100</MenuItem>
            </Select>
          </FormControl>

          <FormControl fullWidth margin="normal">
            <FormLabel>{t('common:preferences.tables.defaultSortOrder')}</FormLabel>
            <Select
              value={preferences.tables.defaultSortOrder}
              onChange={(e) => updatePreference('tables', 'defaultSortOrder', e.target.value)}
            >
              <MenuItem value="asc">{t('common:preferences.tables.ascending')}</MenuItem>
              <MenuItem value="desc">{t('common:preferences.tables.descending')}</MenuItem>
            </Select>
          </FormControl>

          <Box sx={{ mt: 3 }}>
            <Typography gutterBottom>
              {t('common:preferences.tables.refreshInterval')}
            </Typography>
            <Slider
              value={preferences.tables.refreshInterval}
              onChange={(e, value) => updatePreference('tables', 'refreshInterval', value)}
              min={10}
              max={300}
              step={10}
              marks={[
                { value: 10, label: '10s' },
                { value: 60, label: '1m' },
                { value: 300, label: '5m' }
              ]}
              valueLabelDisplay="auto"
            />
          </Box>

          <FormGroup sx={{ mt: 2 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={preferences.tables.showRowNumbers}
                  onChange={(e) => updatePreference('tables', 'showRowNumbers', e.target.checked)}
                />
              }
              label={t('common:preferences.tables.showRowNumbers')}
            />
            <FormControlLabel
              control={
                <Switch
                  checked={preferences.tables.stickyHeaders}
                  onChange={(e) => updatePreference('tables', 'stickyHeaders', e.target.checked)}
                />
              }
              label={t('common:preferences.tables.stickyHeaders')}
            />
            <FormControlLabel
              control={
                <Switch
                  checked={preferences.tables.autoRefresh}
                  onChange={(e) => updatePreference('tables', 'autoRefresh', e.target.checked)}
                />
              }
              label={t('common:preferences.tables.autoRefresh')}
            />
          </FormGroup>
        </CardContent>
      </Card>
    </Box>
  );

  // Dashboard Tab
  const renderDashboardTab = () => (
    <Box sx={{ p: 2 }}>
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            <PaletteIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
            {t('common:preferences.dashboard.title')}
          </Typography>
          
          <Box sx={{ mt: 3 }}>
            <Typography gutterBottom>
              {t('common:preferences.dashboard.refreshInterval')}
            </Typography>
            <Slider
              value={preferences.dashboard.dashboardRefreshInterval}
              onChange={(e, value) => updatePreference('dashboard', 'dashboardRefreshInterval', value)}
              min={30}
              max={600}
              step={30}
              marks={[
                { value: 30, label: '30s' },
                { value: 60, label: '1m' },
                { value: 300, label: '5m' },
                { value: 600, label: '10m' }
              ]}
              valueLabelDisplay="auto"
            />
          </Box>

          <FormGroup sx={{ mt: 2 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={preferences.dashboard.autoRefreshDashboard}
                  onChange={(e) => updatePreference('dashboard', 'autoRefreshDashboard', e.target.checked)}
                />
              }
              label={t('common:preferences.dashboard.autoRefresh')}
            />
            <FormControlLabel
              control={
                <Switch
                  checked={preferences.dashboard.showWelcomeMessage}
                  onChange={(e) => updatePreference('dashboard', 'showWelcomeMessage', e.target.checked)}
                />
              }
              label={t('common:preferences.dashboard.showWelcomeMessage')}
            />
            <FormControlLabel
              control={
                <Switch
                  checked={preferences.dashboard.compactWidgets}
                  onChange={(e) => updatePreference('dashboard', 'compactWidgets', e.target.checked)}
                />
              }
              label={t('common:preferences.dashboard.compactWidgets')}
            />
          </FormGroup>
        </CardContent>
      </Card>
    </Box>
  );

  const tabs = [
    { label: t('common:preferences.tabs.display'), icon: <DisplayIcon />, content: renderDisplayTab },
    { label: t('common:preferences.tabs.notifications'), icon: <NotificationsIcon />, content: renderNotificationsTab },
    { label: t('common:preferences.tabs.tables'), icon: <TableIcon />, content: renderTablesTab },
    { label: t('common:preferences.tabs.dashboard'), icon: <PaletteIcon />, content: renderDashboardTab }
  ];

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          maxHeight: '90vh'
        }
      }}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <SettingsIcon sx={{ mr: 1 }} />
          {t('common:preferences.title')}
        </Box>
        <Box>
          <IconButton onClick={resetPreferences} color="warning" size="small">
            <ResetIcon />
          </IconButton>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ p: 0 }}>
        {hasChanges && (
          <Alert severity="info" sx={{ m: 2, mb: 0 }}>
            {t('common:preferences.unsavedChanges')}
          </Alert>
        )}

        <Tabs
          value={activeTab}
          onChange={(e, newValue) => setActiveTab(newValue)}
          variant="scrollable"
          scrollButtons="auto"
          sx={{ borderBottom: 1, borderColor: 'divider' }}
        >
          {tabs.map((tab, index) => (
            <Tab
              key={index}
              label={tab.label}
              icon={tab.icon}
              iconPosition="start"
            />
          ))}
        </Tabs>

        <Box sx={{ minHeight: 400 }}>
          {tabs[activeTab]?.content()}
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 2, gap: 1 }}>
        <Button onClick={onClose} color="inherit">{t('common.cancel', 'Cancel')}</Button>
        <Button
          onClick={savePreferences}
          variant="contained"
          disabled={!hasChanges}
        >
          {t('common.save', 'Save')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default React.memo(UserPreferences);

